import * as React from 'react';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import { auth, useFirebaseAuthentication } from 'lib/firebase';
import { useSnackbar } from 'notistack';
import { getAuth, signInWithEmailAndPassword } from "firebase/auth";
import { useLocation } from 'react-router-dom';
import SiteWrapper from './home-pages/SiteWrapper';

function Copyright(props) {
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      align="center"
      {...props}
      sx={{
        color: "rgba(255, 255, 255, 0.3)",
      }}
    >
      {"Copyright © "}
      <Link
        color="inherit"
        href="https://www.ryandymock.com/"
        sx={{
          color: "rgba(255, 255, 255, 0.5)",
          textDecoration: "none",
          transition: "color 0.2s ease",
          "&:hover": {
            color: "#6366f1",
          },
        }}
      >
        RyanDymock.com
      </Link>{" "}
      {new Date().getFullYear()}
      {"."}
    </Typography>
  );
}

const theme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#6366f1",
    },
    secondary: {
      main: "#ec4899",
    },
    background: {
      default: "#07090e",
      paper: "rgba(255, 255, 255, 0.02)",
    },
    text: {
      primary: "#ffffff",
      secondary: "rgba(255, 255, 255, 0.7)",
    },
  },
  typography: {
    fontFamily: "'Kanit', sans-serif",
  },
});

export const SignInPage = (props) => {
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const authUser = useFirebaseAuthentication()

  const loc = useLocation()
  // const redirect = loc.pathname === '/signin' ? "/" : loc.pathname;
  const redirect = loc.state?.from?.pathname || "/" // Get the page they were trying to access

  const handleSubmit = (event) => {
    event.preventDefault()
    const data = new FormData(event.currentTarget)
    const email = data.get("email")
    const password = data.get("password")
    signInWithEmailAndPassword(auth, email, password)
      .then((user) => {
        //check for the groupID / familyID here
        //check for family setup completion
        if (user.user.emailVerified) {
          // getUserData(user.user.uid, onUserDataReceived);
          localStorage.setItem("loggedIn", "TRUE")
          // console.log(`redirecting to ${redirect}`)
          navigate(redirect)
        } else {
          showErrorMessage(
            "Please first check your email and verify your registration.  Then try again!"
          )
          // sendConfirmationEmail(user.user); //
          getAuth().signOut()
          navigate("/signIn")
        }
      })
      .catch((error) => {
        handleBadLogin(error)
      })
  }

  if (authUser && authUser.emailVerified) {
    // getUserData(authUser.uid, onUserDataReceived);
  }

  const showErrorMessage = (message) => {
    enqueueSnackbar(message, {
      variant: "error",
      anchorOrigin: {
        vertical: "top",
        horizontal: "right",
      },
    })
  }

  const handleBadLogin = (error) => {
    if (error.code === "auth/invalid-email") {
      showErrorMessage("please enter a valid email address")
    } else if (error.code === "auth/user-not-found") {
      showErrorMessage(
        "Hmm...  We don't recognize that email address, please try again!"
      )
    } else if (error.code === "auth/internal-error") {
      showErrorMessage("Unable to process your request, please try again!")
    } else if (error.code === "auth/wrong-password") {
      showErrorMessage(
        "Hmm...  You may have typed your password incorrectly, please try again!"
      )
    } else {
      showErrorMessage(error.message)
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <SiteWrapper>
        <Container
          component="main"
          maxWidth="xs"
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            minHeight: "80vh",
            py: 4,
          }}
        >
          <CssBaseline />
          <Box
            sx={{
              background: "rgba(255, 255, 255, 0.02)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255, 255, 255, 0.08)",
              borderRadius: "24px",
              p: { xs: 3, sm: 5 },
              boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.37), inset 0 0 0 1px rgba(255, 255, 255, 0.05)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "4px",
                background: "linear-gradient(90deg, #6366f1 0%, #ec4899 100%)",
              },
            }}
          >
            {/* Header/Logo Icon */}
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "20px",
                bgcolor: "rgba(99, 102, 241, 0.1)",
                border: "1px solid rgba(99, 102, 241, 0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#6366f1",
                mb: 3,
                boxShadow: "0 0 20px rgba(99, 102, 241, 0.2)",
                transition: "transform 0.3s ease-in-out",
                "&:hover": {
                  transform: "rotate(10deg) scale(1.05)",
                },
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: "28px" }} />
            </Box>

            <Typography
              component="h1"
              variant="h4"
              sx={{
                fontWeight: 800,
                fontFamily: "'Kanit', sans-serif",
                background: "linear-gradient(to right, #fff 60%, rgba(255,255,255,0.7) 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                letterSpacing: "-0.02em",
                mb: 1,
              }}
            >
              Welcome Back
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "rgba(255, 255, 255, 0.5)",
                mb: 4,
                textAlign: "center",
                fontWeight: 300,
              }}
            >
              Sign in to access your family portal
            </Typography>

            <Box
              component="form"
              onSubmit={handleSubmit}
              noValidate
              sx={{ mt: 1, width: "100%" }}
            >
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                variant="outlined"
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    color: "#fff",
                    backgroundColor: "rgba(255, 255, 255, 0.02)",
                    borderRadius: "14px",
                    "& fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.08)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.2)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#6366f1",
                      boxShadow: "0 0 0 4px rgba(99, 102, 241, 0.15)",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "rgba(255, 255, 255, 0.4)",
                    "&.Mui-focused": {
                      color: "#6366f1",
                    },
                  },
                }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
                variant="outlined"
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    color: "#fff",
                    backgroundColor: "rgba(255, 255, 255, 0.02)",
                    borderRadius: "14px",
                    "& fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.08)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.2)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#6366f1",
                      boxShadow: "0 0 0 4px rgba(99, 102, 241, 0.15)",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "rgba(255, 255, 255, 0.4)",
                    "&.Mui-focused": {
                      color: "#6366f1",
                    },
                  },
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  mt: 2,
                  mb: 3,
                  py: 1.8,
                  borderRadius: "14px",
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  letterSpacing: "0.05em",
                  textTransform: "none",
                  background: "linear-gradient(135deg, #6366f1 0%, #a855f7 100%)",
                  boxShadow: "0 4px 15px rgba(99, 102, 241, 0.3)",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    background: "linear-gradient(135deg, #4f46e5 0%, #9333ea 100%)",
                    boxShadow: "0 6px 20px rgba(99, 102, 241, 0.45)",
                    transform: "translateY(-2px)",
                  },
                  "&:active": {
                    transform: "translateY(0)",
                  },
                }}
              >
                Sign In
              </Button>
              <Grid container sx={{ mt: 1 }}>
                <Grid item xs>
                  <Link
                    href="#"
                    variant="body2"
                    sx={{
                      color: "rgba(255, 255, 255, 0.5)",
                      textDecoration: "none",
                      transition: "color 0.2s ease",
                      "&:hover": {
                        color: "#6366f1",
                      },
                    }}
                  >
                    Forgot password?
                  </Link>
                </Grid>
                <Grid item>
                  <Link
                    href="/signUp"
                    variant="body2"
                    sx={{
                      color: "#ec4899",
                      textDecoration: "none",
                      fontWeight: 600,
                      transition: "all 0.2s ease",
                      "&:hover": {
                        color: "#f472b6",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    {"Don't have an account? Sign Up"}
                  </Link>
                </Grid>
              </Grid>
            </Box>
          </Box>
          <Copyright sx={{ mt: 5 }} />
        </Container>
      </SiteWrapper>
    </ThemeProvider>
  )
}